// AUTO-EXTRACTED via in-session WebFetch (Claude Opus 4.7) from satel.pl
// product pages, 2026-05-16. Eleven Satel alarm control panels missing
// from the hand-curated SATEL_EQUIPMENT_CATALOG.
//
// All entries set verified: false. Promote to verified: true after the
// reviewer spot-checks specs against the linked product page.
//
// Notes from the run:
//  - INTEGRA 24 + 128-WRL: product page does not state EN 50131 grade.
//    Set to 2 per Satel's standard certification for non-Plus Integra
//    models (matches the existing `satel-integra-128` entry convention).
//    Reviewer should re-confirm from Satel's certificate registry.
//  - VERSA Plus / PERFECTA 16 etc. ship with 2G GSM/GPRS onboard, mapped
//    to "GSM-LTE" enum value to stay compatible with AlarmPanelSpec
//    (the existing SATEL_EQUIPMENT_CATALOG follows the same convention).
//  - INTEGRA 128-WRL's mobileApp left undefined — page does not name it.
import type { EquipmentCatalogEntry } from '../equipmentCatalogEntry';

const SATEL_PRODUCT =
  'https://www.satel.pl/en/products/intruder-alarms';

export const SATEL_ALARM_PANEL_AUTO: EquipmentCatalogEntry[] = [
  // ── INTEGRA series — flagship modular panels ───────────────────────────
  {
    id: 'satel-integra-24',
    manufacturer: 'SATEL',
    model: 'INTEGRA 24',
    category: 'alarm-panel',
    tagline: '24 zones · 4 partitions · onboard PSTN dialer',
    verified: false,
    datasheetUrl: `${SATEL_PRODUCT}/integra/alarm-control-panels/integra-24/`,
    specs: {
      series: 'INTEGRA',
      zonesOnboard: 4,
      zonesMax: 24,
      partitions: 4,
      outputsOnboard: 4,
      outputsMax: 20,
      users: 17,
      grade: 2,
      communicators: ['PSTN'],
      psuAmps: 1.2,
    },
  },
  {
    id: 'satel-integra-128-wrl',
    manufacturer: 'SATEL',
    model: 'INTEGRA 128-WRL',
    category: 'alarm-panel',
    tagline: '128 zones · 32 partitions · onboard ABAX wireless + GSM',
    verified: false,
    datasheetUrl: `${SATEL_PRODUCT}/integra/alarm-control-panels/integra-128-wrl/`,
    specs: {
      series: 'INTEGRA',
      zonesOnboard: 8,
      zonesMax: 128,
      partitions: 32,
      outputsOnboard: 8,
      outputsMax: 128,
      users: 240,
      grade: 2,
      communicators: ['GSM-LTE', 'wireless'],
      psuAmps: 2,
    },
  },

  // ── VERSA series — residential / small commercial ─────────────────────
  {
    id: 'satel-versa-5',
    manufacturer: 'SATEL',
    model: 'VERSA 5',
    category: 'alarm-panel',
    tagline: '5-30 zones · 2 partitions · Grade 2, onboard IP/PSTN/GSM',
    verified: false,
    datasheetUrl: `${SATEL_PRODUCT}/versa/alarm-control-panels/versa-5/`,
    specs: {
      series: 'VERSA',
      zonesOnboard: 5,
      zonesMax: 30,
      partitions: 2,
      outputsOnboard: 4,
      outputsMax: 12,
      users: 31,
      grade: 2,
      communicators: ['PSTN', 'Ethernet', 'GSM-LTE'],
      psuAmps: 1,
    },
  },
  {
    id: 'satel-versa-plus',
    manufacturer: 'SATEL',
    model: 'VERSA Plus',
    category: 'alarm-panel',
    tagline: '4-30 zones · 2 partitions · Grade 2, onboard IP/GSM/PSTN',
    verified: false,
    datasheetUrl: `${SATEL_PRODUCT}/versa/alarm-control-panels/versa-plus/`,
    specs: {
      series: 'VERSA',
      zonesOnboard: 4,
      zonesMax: 30,
      partitions: 2,
      outputsOnboard: 5,
      outputsMax: 12,
      users: 30,
      grade: 2,
      communicators: ['PSTN', 'Ethernet', 'GSM-LTE'],
      mobileApp: 'VERSA CONTROL',
    },
  },

  // ── PERFECTA series — entry-level dialler panels ───────────────────────
  {
    id: 'satel-perfecta-t-32',
    manufacturer: 'SATEL',
    model: 'PERFECTA-T 32',
    category: 'alarm-panel',
    tagline: '8-32 zones · 2 partitions · Grade 2 · PSTN dialer',
    verified: false,
    datasheetUrl: `${SATEL_PRODUCT}/perfecta/alarm-control-panels/perfecta-t-32/`,
    specs: {
      series: 'PERFECTA',
      zonesOnboard: 8,
      zonesMax: 32,
      partitions: 2,
      outputsOnboard: 4,
      outputsMax: 12,
      users: 15,
      grade: 2,
      communicators: ['PSTN'],
    },
  },
  {
    id: 'satel-perfecta-t-32-wrl',
    manufacturer: 'SATEL',
    model: 'PERFECTA-T 32-WRL',
    category: 'alarm-panel',
    tagline: '32 zones · 2 partitions · Grade 2 · 433 MHz wireless + PSTN',
    verified: false,
    datasheetUrl: `${SATEL_PRODUCT}/perfecta/alarm-control-panels/perfecta-t-32-wrl/`,
    specs: {
      series: 'PERFECTA',
      zonesOnboard: 8,
      zonesMax: 32,
      partitions: 2,
      outputsOnboard: 4,
      outputsMax: 12,
      users: 15,
      grade: 2,
      communicators: ['PSTN', 'wireless'],
    },
  },
  {
    id: 'satel-perfecta-32',
    manufacturer: 'SATEL',
    model: 'PERFECTA 32',
    category: 'alarm-panel',
    tagline: '32 zones · 2 partitions · Grade 2 · dual-SIM GSM + PSTN',
    verified: false,
    datasheetUrl: `${SATEL_PRODUCT}/perfecta/alarm-control-panels/perfecta-32/`,
    specs: {
      series: 'PERFECTA',
      zonesOnboard: 8,
      zonesMax: 32,
      partitions: 2,
      outputsOnboard: 4,
      outputsMax: 12,
      users: 15,
      grade: 2,
      communicators: ['PSTN', 'GSM-LTE'],
      mobileApp: 'PERFECTA CONTROL',
    },
  },
  {
    id: 'satel-perfecta-32-wrl',
    manufacturer: 'SATEL',
    model: 'PERFECTA 32-WRL',
    category: 'alarm-panel',
    tagline: '32 zones · 2 partitions · Grade 2 · dual-SIM GSM + 433 MHz wireless',
    verified: false,
    datasheetUrl: `${SATEL_PRODUCT}/perfecta/alarm-control-panels/perfecta-32-wrl/`,
    specs: {
      series: 'PERFECTA',
      zonesOnboard: 8,
      zonesMax: 32,
      partitions: 2,
      outputsOnboard: 4,
      outputsMax: 12,
      users: 15,
      grade: 2,
      communicators: ['GSM-LTE', 'wireless'],
      mobileApp: 'PERFECTA CONTROL',
    },
  },
  {
    id: 'satel-perfecta-32-wrl-lte',
    manufacturer: 'SATEL',
    model: 'PERFECTA 32-WRL LTE',
    category: 'alarm-panel',
    tagline: '32 zones · 2 partitions · Grade 2 · LTE cellular + 433 MHz wireless',
    verified: false,
    datasheetUrl: `${SATEL_PRODUCT}/perfecta/alarm-control-panels/perfecta-32-wrl-lte/`,
    specs: {
      series: 'PERFECTA',
      zonesOnboard: 8,
      zonesMax: 32,
      partitions: 2,
      outputsOnboard: 4,
      outputsMax: 12,
      users: 15,
      grade: 2,
      communicators: ['GSM-LTE', 'wireless'],
      mobileApp: 'PERFECTA CONTROL',
    },
  },
  {
    id: 'satel-perfecta-ip-32-wrl',
    manufacturer: 'SATEL',
    model: 'PERFECTA-IP 32-WRL',
    category: 'alarm-panel',
    tagline: '32 zones · 2 partitions · Grade 2 · Ethernet + 433 MHz wireless',
    verified: false,
    datasheetUrl: `${SATEL_PRODUCT}/perfecta/alarm-control-panels/perfecta-ip-32-wrl/`,
    specs: {
      series: 'PERFECTA',
      zonesOnboard: 8,
      zonesMax: 32,
      partitions: 2,
      outputsOnboard: 4,
      outputsMax: 12,
      users: 15,
      grade: 2,
      communicators: ['Ethernet', 'wireless'],
      mobileApp: 'PERFECTA CONTROL',
    },
  },
  {
    id: 'satel-perfecta-16',
    manufacturer: 'SATEL',
    model: 'PERFECTA 16',
    category: 'alarm-panel',
    tagline: '8-16 zones · 2 partitions · Grade 2 · dual-SIM GSM + PSTN',
    verified: false,
    datasheetUrl: `${SATEL_PRODUCT}/perfecta/alarm-control-panels/perfecta-16/`,
    specs: {
      series: 'PERFECTA',
      zonesOnboard: 8,
      zonesMax: 16,
      partitions: 2,
      outputsOnboard: 4,
      outputsMax: 12,
      users: 15,
      grade: 2,
      communicators: ['PSTN', 'GSM-LTE'],
      mobileApp: 'PERFECTA CONTROL',
    },
  },
];
